// Step 6: everything in one place, then off to the ops tablet. Nothing reaches the
// tablet's alerts or any phone until someone approves it on the ops tablet.

import { useState } from 'react';
import type { SiteDraft } from '../../../../src/draft.ts';
import { api, message } from '../../api.ts';
import { languageProgress } from '../../../../src/draft.ts';
import { day, when } from '../../format.ts';
import { languageList } from '../../languages.ts';
import type { SiteView } from '../../types.ts';
import { Link } from '../../ui.tsx';

/** "10.0 × 8.0 km" */
export function areaText(bbox: [number, number, number, number]): string {
  const [w, s, e, n] = bbox;
  const mid = ((s + n) / 2) * (Math.PI / 180);
  const km = (x: number) => x.toFixed(1);
  return `${km((e - w) * 111.32 * Math.cos(mid))} × ${km((n - s) * 110.574)} km`;
}

export function ReviewStep({ site, reload }: { site: SiteView; reload: () => void }) {
  const draft = site.draft as SiteDraft;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const unchecked = Object.keys(draft.messages).filter((l) => !draft.checked.includes(l));
  const unfinished = Object.keys(draft.messages).filter((l) => !languageProgress(draft, l).complete);
  const waiting = site.changes.find((c) => c.status === 'waiting');
  const last = site.changes.find((c) => c.status !== 'waiting');
  // Who approves: for a new site, its own tablet; after go-live, a tablet already in the
  // live version (the phones insist on it).
  const approvers = site.bundle ? site.bundle.tablets : draft.tablets;

  const rows: { label: string; value: string; step: string; amber?: boolean }[] = [
    { label: 'Site', value: `${site.siteName} · ${site.clientName}`, step: 'details' },
    { label: 'Languages', value: languageList(Object.keys(draft.messages)), step: 'wording' },
    {
      label: 'Map',
      value: !draft.map
        ? 'Not chosen yet'
        : !draft.picture
          ? `No picture yet · ${areaText(draft.map.bbox)}`
          : draft.picture.source === 'client'
            ? `The client's own map · ${areaText(draft.map.bbox)}`
            : `Satellite picture from ${day(draft.picture.capturedOn)} · ${areaText(draft.map.bbox)}`,
      step: 'map',
      amber: !draft.map || !draft.picture,
    },
    {
      label: 'Zones',
      value: draft.zones.length ? `${draft.zones.length} zone${draft.zones.length === 1 ? '' : 's'}: ${draft.zones.map((z) => `${z.id} ${z.name}`).join(', ')}` : 'None yet',
      step: 'zones',
      amber: draft.zones.length === 0,
    },
    {
      label: 'Alert wording',
      value: unfinished.length
        ? `${languageList(unfinished)} ${unfinished.length === 1 ? "isn't" : "aren't"} finished yet`
        : unchecked.length
          ? `${languageList(unchecked)} still need${unchecked.length === 1 ? 's' : ''} checking by someone fluent`
          : 'Every language complete and checked',
      step: 'wording',
      amber: unfinished.length > 0 || unchecked.length > 0,
    },
    {
      label: 'Ops tablet',
      value: draft.tablets.length ? draft.tablets.map((t) => t.label).join(', ') : 'None yet',
      step: 'tablet',
      amber: draft.tablets.length === 0,
    },
  ];

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ summary: string }>('POST', `/sites/${site.siteId}/send`, {});
      setSent(r.summary);
      reload();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  const withdraw = async (id: string) => {
    if (!confirm('Withdraw this change? It will no longer be on the ops tablet to approve.')) return;
    setError(null);
    try {
      await api('POST', `/changes/${id}/withdraw`, {});
      setSent(null);
      reload();
    } catch (e) {
      setError(message(e));
    }
  };

  return (
    <>
      <div className="table" style={{ maxWidth: 860 }}>
        {rows.map((r) => (
          <div key={r.label} className="tr" style={{ gridTemplateColumns: '190px minmax(0, 1fr) auto', alignItems: 'start' }}>
            <span className="muted">{r.label}</span>
            <span className={r.amber ? 'amber' : undefined}>{r.value}</span>
            <Link to={`/sites/${site.siteId}/setup/${r.step}`} className="linkish small">
              Change
            </Link>
          </div>
        ))}
      </div>

      {site.readiness.blocking.length > 0 && (
        <div className="note amber" style={{ maxWidth: 860 }}>
          <strong>Before it can be sent for approval:</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
            {site.readiness.blocking.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </div>
      )}
      {site.readiness.warnings.length > 0 && (
        <div className="note" style={{ maxWidth: 860 }}>
          <strong>Worth knowing (these don't stop it):</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
            {site.readiness.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {waiting ? (
        <div className="card" style={{ maxWidth: 860, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 20 }}>
          <div className="stack" style={{ gap: 6 }}>
            <span className="amber small" style={{ fontWeight: 500 }}>
              Waiting for approval on {approvers.length === 1 ? approvers[0].label : 'the ops tablet'}
            </span>
            <span style={{ fontWeight: 500 }}>{sent ?? waiting.summary}</span>
            <span className="muted small">
              Version {waiting.version} · sent by {waiting.preparedBy} · {when(waiting.preparedAt)}. It's on the tablet now, with the map and what changes. Nothing changes until
              someone approves it there. Edits made now go in the next change.
            </span>
          </div>
          <button className="btn small" onClick={() => withdraw(waiting.changeId)}>
            Withdraw
          </button>
        </div>
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          {last?.status === 'approved' && (
            <span className="chip green" style={{ alignSelf: 'flex-start' }}>
              Version {last.version} approved on {last.decidedBy ?? 'the ops tablet'}{site.live ? ' · the site is live' : ''}
            </span>
          )}
          <div className="row">
            <button className="btn primary" disabled={busy || site.readiness.blocking.length > 0} onClick={send}>
              {site.live ? 'Send the changes to the ops tablet' : 'Send to the ops tablet for approval'}
            </button>
            {busy && <span className="faint small">Sending…</span>}
          </div>
          <p className="faint small">It appears on the ops tablet with the map and what changes. Someone there checks it and holds to approve.</p>
        </div>
      )}
      {error && <p className="error">{error}</p>}
    </>
  );
}
